import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IMAGE_CONSTANTS, PRIORITY_CONFIG } from '@/constants';
import { formatTimeAgo } from '@/utils/dateUtils';

import type { TaskDetail } from '@/types';

interface InProgressTaskDetailModalProps {
  task: TaskDetail;
  onClose: () => void;
  onCancel: () => void;
  onComplete: () => void;
}

/**
 * Xamarin color constants (App.xaml):
 *  - InProgressTaskBodyBackgroundColor: linear-gradient(#094888 → #0B1B25)
 *  - TaskHeaderColor: #E7EDF4
 *  - TaskHeaderSrcDesBackgroundColor: #EDF6FF
 *  - TaskHeaderTextColor: #0B2F50
 *  - TaskSeperationLineBackgroundColor: #C3D2DC
 *  - InprogressTaskNoteBackgroundColor: #10273C
 *  - MoveTaskBackgroundColor: #E38C25
 *  - AuditTaskBackgroundColor: #273F94
 *  - PrimaryBackgroundColor: #003366
 *  - LoginButtonGreenColor: #36AB00
 *  - CriticalPriorityDarkColor: #FC3B3B
 *  - HighPriorityDarkColor: #EDBE18
 */

// ─────────────────────────────────────────────────────────────────

// Matches Xamarin CreatedTimeToCreatedTaskAgoConverter → YMSHelperMethods.TimeSpanCalculator.
// formatTimeAgo imported from @/utils/dateUtils

// ─────────────────────────────────────────────────────────────────

/**
 * Get the priority label and color for the in-progress task badge.
 * Matches Xamarin TaskPriorityToLabelConverter + InprogressTaskPrioritytoIconConverter.
 */
function getPriorityInfo(
  priority: number,
  t: (key: string) => string
): { label: string; bgColor: string; icon: string } | null {
  if (priority >= PRIORITY_CONFIG.CRITICAL.min && priority <= PRIORITY_CONFIG.CRITICAL.max) {
    return {
      label: t('logoutSummary.critical'),
      bgColor: '#FC3B3B',
      icon: IMAGE_CONSTANTS.criticalWhiteIcon,
    };
  }
  if (priority >= PRIORITY_CONFIG.HIGH.min && priority <= PRIORITY_CONFIG.HIGH.max) {
    return {
      label: t('logoutSummary.highPriority'),
      bgColor: '#EDBE18',
      icon: IMAGE_CONSTANTS.highWhiteIcon,
    };
  }
  return null; // Standard priority — no badge shown
}

// ─────────────────────────────────────────────────────────────────

/**
 * In-progress task detail modal — matches Xamarin DashboardPage.xaml BottomSheetControl.
 *
 * Xamarin layout:
 *  - BottomSheetControl slides up from bottom
 *  - Row 0: Expand icon (24px phone) — tap to close
 *  - Row 1: ScrollView containing the task detail card
 *    - Outer Frame: CornerRadius=10, transparent bg + white inner frame
 *    - Inner Grid: InProgressTaskBodyBackgroundColor (gradient #094888 → #0B1B25)
 *      - Row 0: Header (80px, TaskHeaderColor #E7EDF4)
 *        - Col 0 (0.2*): Task type badge (icon 30px + type label, colored bg)
 *        - Col 1 (*): Source frame → transit/arrow → Destination frame
 *      - Row 1: Task description (white, 12px phone, Margin="20,10")
 *      - Row 2: Separator (1px, #C3D2DC)
 *      - Row 3: Detail grid (3 cols, Margin="20,10"):
 *        - TaskId | Source/Destination | Destination (for Move)
 *        - CarrierName | TrailerNumber (if trailer exists)
 *        - TrailerComments frame (bg=#10273C, white text)
 *      - Row 4: Separator
 *      - Row 5: Cancel + Complete buttons (H=40, phone fills width)
 *      - Row 6: Priority badge + Created time ago (phone only)
 */
export function InProgressTaskDetailModal({
  task,
  onClose,
  onCancel,
  onComplete,
}: InProgressTaskDetailModalProps) {
  const { t } = useTranslation();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const isMove = task.TaskType === 'Move';
  const priorityInfo = getPriorityInfo(task.Priority, t);
  const [isClosing, setIsClosing] = useState(false);
  const pendingCallback = useRef<(() => void) | null>(null);

  const CLOSE_DURATION = 300;

  const animateClose = useCallback(
    (callback: () => void) => {
      if (isClosing) return;
      setIsClosing(true);
      pendingCallback.current = callback;
      setTimeout(() => {
        pendingCallback.current?.();
      }, CLOSE_DURATION);
    },
    [isClosing]
  );

  const handleClose = useCallback(() => {
    animateClose(onClose);
  }, [animateClose, onClose]);
  const handleCancel = useCallback(() => {
    animateClose(onCancel);
  }, [animateClose, onCancel]);
  const handleComplete = useCallback(() => {
    animateClose(onComplete);
  }, [animateClose, onComplete]);

  // Source / Destination labels for the header frames
  const sourceName = isMove ? task.MoveSourceName : task.AuditLocationName;
  const sourceType = isMove ? task.MoveSourceType : t('dashboard.yardJockey');
  const destName = isMove ? task.MoveDestinationName : task.AuditLocationName;
  const destType = isMove ? task.MoveDestinationType : task.AuditLocationType;

  return (
    /* Backdrop — semi-transparent overlay, stops above the 60px bottom nav bar */
    <div
      className={`fixed inset-0 bottom-[60px] z-40 bg-black/40 flex flex-col justify-end touch-none ${isClosing ? 'animate-out fade-out duration-300' : 'animate-in fade-in duration-200'}`}
    >
      {/* Bottom sheet container — fixed max height with internal scroll to prevent resize on scroll */}
      <div
        className={`bg-white rounded-t-[5px] max-h-[85vh] flex flex-col overflow-hidden ${isClosing ? 'animate-out slide-out-to-bottom duration-300' : 'animate-in slide-in-from-bottom duration-300'}`}
      >
        {/* ── Row 0: Expand/collapse handle — Xamarin: expand.svg, HeightRequest=24 (phone), tap=CloseBottomSheet ── */}
        <button type="button" onClick={handleClose} className="shrink-0 py-1 w-full">
          <img src={IMAGE_CONSTANTS.expand} alt="Close" className="h-6 w-full object-contain" />
        </button>

        {/* ── Row 1: Content — scrollable area ── */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-auto">
          {/* Outer frame — Xamarin: Frame CornerRadius=10, transparent bg, Padding=1 */}
          <div className="p-px">
            {/* Inner frame — Xamarin: Frame CornerRadius=10, white bg → Grid with gradient */}
            <div className="rounded-[10px] overflow-hidden">
              {/* Gradient body — Xamarin: InProgressTaskBodyBackgroundColor linear-gradient(#094888 → #0B1B25) */}
              <div
                className="rounded-[10px] pb-3.5"
                style={{ background: 'linear-gradient(135deg, #094888 60%, #0B1B25 100%)' }}
              >
                {/* ══════════════════════════════════════════════════════════
                    Row 0: Header (80px, TaskHeaderColor #E7EDF4)
                    ══════════════════════════════════════════════════════════ */}
                <div className="flex h-[80px]" style={{ backgroundColor: '#E7EDF4' }}>
                  {/* Col 0 (0.2*): Task type badge — colored bg per type */}
                  <div
                    className="flex flex-col items-center justify-center w-[20%] shrink-0"
                    style={{ backgroundColor: isMove ? '#E38C25' : '#273F94' }}
                  >
                    {/* Xamarin: SvgCachedImage HeightRequest=30 */}
                    <img
                      src={isMove ? IMAGE_CONSTANTS.moveTask : IMAGE_CONSTANTS.auditTask}
                      alt={task.TaskType}
                      className="h-[30px] w-[30px] object-contain"
                    />
                    {/* Xamarin: FontSize=14 (phone), white */}
                    <span className="text-white text-sm font-poppins text-center">
                      {task.TaskType}
                    </span>
                  </div>

                  {/* Col 1 (*): Source → transit/arrow → Destination */}
                  <div className="flex flex-1 items-center">
                    {/* Source frame — Xamarin: W=60 (phone)/140 (tablet), bg=#EDF6FF, border=#003366, CornerRadius=10
                        Grid: Col 0 = icon (SourcePinBlue for Move, UserBlue for Audit), Col 1 = name + type */}
                    <div
                      className="w-[60px] md:w-[140px] shrink-0 rounded-[10px] p-[5px] mx-[15px] md:mx-[10px]"
                      style={{ backgroundColor: '#EDF6FF', border: '1px solid #003366' }}
                    >
                      <div className="flex items-start gap-0.5 md:gap-1">
                        <img
                          src={isMove ? IMAGE_CONSTANTS.sourcePinBlue : IMAGE_CONSTANTS.userBlue}
                          alt=""
                          className="h-[15px] w-[15px] md:h-[25px] md:w-[25px] shrink-0 object-contain"
                        />
                        <div className="min-w-0">
                          <p
                            className="text-[8px] md:text-xs font-poppins font-semibold truncate"
                            style={{ color: '#0B2F50' }}
                          >
                            {sourceName}
                          </p>
                          <p
                            className="text-[8px] md:text-[10px] font-poppins truncate"
                            style={{ color: '#0B2F50' }}
                          >
                            {sourceType}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Middle transit section — Xamarin: Frame W=100, Margin=8, transparent bg */}
                    <div className="flex flex-col items-center justify-center w-[60px] md:w-[100px] shrink-0">
                      {isMove && (
                        <>
                          {/* Xamarin: TransitTruck 30px (Move only) */}
                          <img
                            src={IMAGE_CONSTANTS.transitTruck}
                            alt=""
                            className="h-[30px] object-contain"
                          />
                          {/* Xamarin: Arrow 20px */}
                          <img
                            src={IMAGE_CONSTANTS.arrow}
                            alt=""
                            className="h-[20px] object-contain"
                          />
                          {/* Xamarin: TrailerName 8px, PrimaryBackgroundColor #003366 */}
                          <span
                            className="text-[8px] font-poppins text-center truncate w-full"
                            style={{ color: '#003366' }}
                          >
                            {task.TrailerName}
                          </span>
                        </>
                      )}
                      {!isMove && (
                        /* Xamarin: Arrow 20px (Audit only, centered) */
                        <img
                          src={IMAGE_CONSTANTS.arrow}
                          alt=""
                          className="h-[20px] object-contain"
                        />
                      )}
                    </div>

                    {/* Destination frame — same style as source, icon = DestinationPinBlue */}
                    <div
                      className="w-[60px] md:w-[140px] shrink-0 rounded-[10px] p-[5px] mx-[15px] md:mx-[10px]"
                      style={{ backgroundColor: '#EDF6FF', border: '1px solid #003366' }}
                    >
                      <div className="flex items-start gap-0.5 md:gap-1">
                        <img
                          src={IMAGE_CONSTANTS.destinationPinBlue}
                          alt=""
                          className="h-[15px] w-[15px] md:h-[25px] md:w-[25px] shrink-0 object-contain"
                        />
                        <div className="min-w-0">
                          <p
                            className="text-[8px] md:text-xs font-poppins font-semibold truncate"
                            style={{ color: '#0B2F50' }}
                          >
                            {destName}
                          </p>
                          <p
                            className="text-[8px] md:text-[10px] font-poppins truncate"
                            style={{ color: '#0B2F50' }}
                          >
                            {destType}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Col 2: Created time ago — Xamarin: Margin="0,0,20,0", FontSize=16 (tablet), IsVisible=IsTablet */}
                  {task.CreatedDatetime && (
                    <div className="shrink-0 mr-5 flex items-center">
                      <span
                        className="text-[10px] md:text-base font-poppins"
                        style={{ color: '#0B2F50' }}
                      >
                        {formatTimeAgo(task.CreatedDatetime, t)}
                      </span>
                    </div>
                  )}
                </div>

                {/* ══════════════════════════════════════════════════════════
                    Row 1: Task description (white text, 12px phone)
                    Xamarin: Margin="20,10"
                    ══════════════════════════════════════════════════════════ */}
                <div className="px-5 py-2.5">
                  {isMove ? (
                    <p className="text-xs md:text-[26px] md:leading-tight text-white font-poppins">
                      <span>{t('dashboard.moveDescription')} </span>
                      <span className="font-semibold">{task.TrailerName}</span>
                      <span> {t('dashboard.from')} </span>
                      <span className="font-semibold">{task.MoveSourceName}</span>
                      <span> {t('dashboard.to')} </span>
                      <span className="font-semibold">{task.MoveDestinationName}</span>
                      {task.CarrierName.trim() && (
                        <span>
                          {' '}
                          | {task.CarrierCompanyName} - {task.CarrierName}
                        </span>
                      )}
                    </p>
                  ) : task.TrailerName ? (
                    <p className="text-xs md:text-[26px] md:leading-tight text-white font-poppins">
                      <span>{t('dashboard.auditDescriptionWithTrailer')}</span>
                      <span className="font-semibold">{task.TrailerName}</span>
                      <span> {t('dashboard.in')} </span>
                      <span className="font-semibold">{task.AuditLocationName}</span>
                      {task.CarrierName.trim() && (
                        <span>
                          {' '}
                          | {task.CarrierCompanyName} - {task.CarrierName}
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs md:text-[26px] md:leading-tight text-white font-poppins">
                      <span>{t('dashboard.auditDescriptionWithoutTrailer')}</span>
                      <span className="font-bold">{task.AuditLocationName}</span>
                      <span>{t('dashboard.emptyOrNot')}</span>
                    </p>
                  )}
                </div>

                {/* ══════════════════════════════════════════════════════════
                    Row 2: Separator — Xamarin: BoxView HeightRequest=1, #C3D2DC
                    ══════════════════════════════════════════════════════════ */}
                <div className="h-px" style={{ backgroundColor: '#C3D2DC' }} />

                {/* ══════════════════════════════════════════════════════════
                    Row 3: Detail grid (3 columns) — Xamarin: Margin="20,10"
                    InProgressCardTitleLabelStyle: 12px PoppinsBold white
                    InProgressCardValueLabelStyle: 10px PoppinsRegular white
                    ══════════════════════════════════════════════════════════ */}
                <div className="px-5 py-2.5">
                  <div className="grid grid-cols-3 gap-x-2">
                    {/* Row 0, Col 0: Task ID */}
                    <div>
                      <p className="text-xs font-poppins font-bold text-white">
                        {t('dashboard.taskId')}
                      </p>
                      <p className="text-[10px] font-poppins text-white">{task.TaskId}</p>
                    </div>

                    {/* Row 0, Col 1: Source (Move) or Destination (Audit)
                        Xamarin tablet uses SourceLocation/DestLocation labels */}
                    {isMove ? (
                      <div>
                        <p className="text-xs font-poppins font-bold text-white">
                          {t('dashboard.sourceLocation')}
                        </p>
                        <p className="text-[10px] font-poppins text-white">
                          {task.MoveSourceZone} - {task.MoveSourceName} ({task.MoveSourceType})
                        </p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-xs font-poppins font-bold text-white">
                          {t('dashboard.destinationLocation')}
                        </p>
                        <p className="text-[10px] font-poppins text-white">
                          {task.AuditLocationZone} - {task.AuditLocationName} (
                          {task.AuditLocationType})
                        </p>
                      </div>
                    )}

                    {/* Row 0, Col 2: Destination (Move only) */}
                    {isMove && (
                      <div>
                        <p className="text-xs font-poppins font-bold text-white">
                          {t('dashboard.destinationLocation')}
                        </p>
                        <p className="text-[10px] font-poppins text-white">
                          {task.MoveDestinationZone} - {task.MoveDestinationName}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Row 1: Carrier + Trailer (visible only if TrailerName exists) */}
                  {task.TrailerName && (
                    <div className="grid grid-cols-3 gap-x-2 mt-[15px]">
                      <div>
                        <p className="text-xs font-poppins font-bold text-white">
                          {t('dashboard.carrierName')}
                        </p>
                        <p className="text-[10px] font-poppins text-white">
                          {task.CarrierCompanyName}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-poppins font-bold text-white">
                          {t('dashboard.trailerNumber')}
                        </p>
                        <p className="text-[10px] font-poppins text-white">{task.TrailerName}</p>
                      </div>
                    </div>
                  )}

                  {/* Row 2: Trailer Comments — Xamarin: Frame bg=#10273C, border=white, CornerRadius=10,
                      HeightRequest=80, Margin="0,10", Padding=1
                      Label: Padding=10, "Trailer Comments:" bold 10px white + value 10px white */}
                  {task.TrailerComments && (
                    <div
                      className="rounded-[10px] p-px my-2.5 min-h-[80px]"
                      style={{ backgroundColor: '#10273C', border: '1px solid white' }}
                    >
                      <div className="p-2.5">
                        <p className="text-[10px] text-white font-poppins">
                          <span className="font-bold">{t('dashboard.trailerComments')}</span>
                          <span>{task.TrailerComments}</span>
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* ══════════════════════════════════════════════════════════
                    Row 4: Separator
                    ══════════════════════════════════════════════════════════ */}
                <div className="h-px" style={{ backgroundColor: '#C3D2DC' }} />

                {/* ══════════════════════════════════════════════════════════
                    Row 5: Priority badge (tablet, left) + Cancel/Complete buttons (right)
                    Xamarin tablet: priority Frame StartAndExpand + buttons Grid EndAndExpand, W=157
                    Xamarin phone: buttons FillAndExpand (full width)
                    ══════════════════════════════════════════════════════════ */}
                <div className="flex items-center m-5">
                  {/* Priority badge — Xamarin tablet Row 5: Frame CornerRadius=20, Margin=20, Padding="15,8",
                      IsVisible="{OnIdiom Default=true,Phone=false}" */}
                  {priorityInfo && (
                    <div
                      className="flex items-center rounded-full px-[15px] py-[8px] mr-auto"
                      style={{ backgroundColor: priorityInfo.bgColor }}
                    >
                      <img
                        src={priorityInfo.icon}
                        alt=""
                        className="h-[26px] w-[26px] object-contain"
                      />
                      <span className="text-base font-poppins text-white ml-1">
                        {priorityInfo.label}
                      </span>
                    </div>
                  )}

                  {/* Buttons — Xamarin: ColumnSpacing=20, EndAndExpand on tablet */}
                  <div className="flex gap-5 h-10 ml-auto">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="w-[157px] rounded-lg text-sm font-poppins"
                      style={{ backgroundColor: '#FFFFFF', color: '#003366' }}
                    >
                      {t('common.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={handleComplete}
                      className="w-[157px] rounded-lg text-sm font-poppins text-white"
                      style={{ backgroundColor: '#36AB00' }}
                    >
                      {t('common.complete')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
